import { useQuery } from '@apollo/client';
import { OptionalString, parseQueryString, updateQueryString, OptionalBoolean } from '@sixfold/app-utils';
import { Badge, Button, Stack, Input, Select } from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import React from 'react';
import { useLocation, useNavigate } from 'react-router';

import { InfiniteScroll } from '../../components/infinitescroll';
import {
  getNodes,
  loadMoreFromConnection,
  SortDirection,
  TelematicsIntegrationsListQuery,
  TelematicsIntegrationsListQueryVariables,
  TelematicsIntegrationsSortInput,
  TelematicsIntegrationsSortKey,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import { useDebounce } from '../../lib/util/debounce';
import {
  IntegrationList,
  IntegrationListProps as IntegrationListProps,
  SortableColumn,
  useIntegrationListSort,
} from '../components/integration_list';
import { telematicsIntegrationsListQuery } from '../graphql';

import styles from './styles.module.css';

class IntegrationsListLoadingContainer extends Loader<
  TelematicsIntegrationsListQuery,
  { integrations: IntegrationListProps['integrations']; countMatchingFilter: number },
  object
> {}

export const TelematicsIntegrationsContainer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeFilters = parseQueryString({
    queryString: location.search,
    queryParametersToParse: {
      search: OptionalString,
      enabled: OptionalBoolean,
    },
  });
  const clearFilters = () => void navigate(location.pathname);

  const setActiveFilters = (name: keyof typeof activeFilters, value: string | boolean | undefined) => {
    void navigate(
      `${location.pathname}?${updateQueryString(location.search, {
        param: name,
        value,
      })}`,
    );
  };

  const { sort } = useIntegrationListSort();

  const debouncedSearch = useDebounce(activeFilters.search, 500);

  const statusOptions = [
    { label: 'Enabled', value: 'true' },
    { label: 'Disabled', value: 'false' },
  ];

  const result = useQuery<TelematicsIntegrationsListQuery, TelematicsIntegrationsListQueryVariables>(
    telematicsIntegrationsListQuery,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        limit: 500,
        textSearch: debouncedSearch ?? undefined,
        sort: sortToGraphql(sort),
        isEnabled: activeFilters.enabled,
      },
    },
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)' }}>
      <Stack direction="row" alignItems="center" gap="small" wrap={true}>
        <div className={styles.searchInput}>
          <Input
            autoFocus
            size="small"
            name="integrations-search"
            label="Integrations search"
            labelPosition="hidden"
            placeholder="Integrations search..."
            value={activeFilters.search ?? ''}
            onChange={({ value }) => {
              setActiveFilters('search', value || undefined);
            }}
          />
        </div>
        <Select
          label="Status"
          labelPosition="hidden"
          placeholder="Status"
          isClearable
          options={statusOptions}
          value={statusOptions.find((o) => o.value === activeFilters.enabled?.toString()) ?? null}
          onChange={(option) => {
            setActiveFilters('enabled', option ? option.value === 'true' : undefined);
          }}
          className={styles.compactFilterSelect}
        />
        {Object.values(activeFilters).some(notNil) && (
          <Button onClick={clearFilters} size="medium">
            Clear filters
          </Button>
        )}
      </Stack>

      <IntegrationsListLoadingContainer
        result={
          {
            ...result,
            loading: result.networkStatus === 1 || result.networkStatus === 2 ? result.loading : false,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any
        }
        mapData={({ data }) => ({
          integrations: getNodes(data.telematicsIntegrations?.integrations),
          countMatchingFilter: data.telematicsIntegrations?.count ?? 0,
        })}>
        {({ integrations, countMatchingFilter }) => (
          <Stack direction="column" gap="medium" style={{ flex: 1, minHeight: 0 }}>
            <Badge size="medium" kind="neutral">
              {(integrations?.length ?? 0) < countMatchingFilter
                ? `Showing ${integrations?.length ?? 0} out of ${countMatchingFilter} integrations`
                : `${countMatchingFilter} integrations`}
            </Badge>
            <InfiniteScroll
              className="integrations-list"
              triggerOnWindow={false}
              loadMoreEntries={() =>
                loadMoreFromConnection(telematicsIntegrationsListQuery, result, [
                  'telematicsIntegrations',
                  'integrations',
                ])
              }>
              <IntegrationList hideCredentials integrations={integrations} />
            </InfiniteScroll>
          </Stack>
        )}
      </IntegrationsListLoadingContainer>
    </div>
  );
};

export const sortToGraphql = (
  sort: ReturnType<typeof useIntegrationListSort>['sort'],
): TelematicsIntegrationsSortInput => {
  return {
    key: sortToGraphqlMap[sort.key],
    direction: sort.direction === 'ASC' ? SortDirection.ASCENDING : SortDirection.DESCENDING,
  };
};

const sortToGraphqlMap = {
  telematics_integration_id: TelematicsIntegrationsSortKey.INTEGRATION_ID,
  telematics_provider_id: TelematicsIntegrationsSortKey.PROVIDER_ID,
  company_id: TelematicsIntegrationsSortKey.COMPANY_NAME,
  createdAt: TelematicsIntegrationsSortKey.CREATED_AT,
  updatedAt: TelematicsIntegrationsSortKey.UPDATED_AT,
} satisfies {
  [K in SortableColumn]: TelematicsIntegrationsSortInput['key'];
};
